import React, {
  createContext,
  useContext,
  ChangeEvent,
  useState,
  KeyboardEvent
} from 'react';
import { ColumnDef } from '@tanstack/react-table';
import AdvanceTable from 'components/base/AdvanceTable';
import AdvanceTableFooter from 'components/base/AdvanceTableFooter';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import useAdvanceTable from 'hooks/useAdvanceTable';
import RevealDropdown from 'components/base/RevealDropdown';
import { Dropdown, Modal, Button, Form } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash, faEdit, faPlus } from '@fortawesome/free-solid-svg-icons';
import SearchBox from 'components/common/SearchBox';
import {
  createBXPOutboundRule,
  updateBXPOutboundRule,
  deleteBXPOutboundRule,
  BXPOutboundRule
} from 'services/hvaservicecampaign.service';

interface OutboundRulesTableProps {
  outboundRules: BXPOutboundRule[];
  onOutboundRulesChange: () => Promise<void>;
}

const OutboundRulesChangeContext = createContext<
  (() => Promise<void>) | undefined
>(undefined);

const useOutboundRulesChange = () => {
  const context = useContext(OutboundRulesChangeContext);
  if (context === undefined) {
    throw new Error(
      'useOutboundRulesChange must be used within an OutboundRulesChangeProvider'
    );
  }
  return context;
};

type OutboundRuleActionCellProps = {
  outboundRule: BXPOutboundRule;
};

const OutboundRuleActionCell = ({
  outboundRule
}: OutboundRuleActionCellProps) => {
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  // eslint-disable-next-line
  const [selectedOutboundRule, setSelectedOutboundRule] = useState<BXPOutboundRule | null>(null);
  const onOutboundRulesChange = useOutboundRulesChange();

  const handleCloseDeleteModal = () => setShowDeleteModal(false);
  const handleShowDeleteModal = () => setShowDeleteModal(true);

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setSelectedOutboundRule(null);
  };

  const handleShowEditModal = () => {
    setSelectedOutboundRule(outboundRule);
    setShowEditModal(true);
  };

  const handleConfirmRemove = async () => {
    try {
      await deleteBXPOutboundRule(outboundRule.id);
      console.log(
        `Outbound Rule with ID: ${outboundRule.id} deleted successfully`
      );
      await onOutboundRulesChange();
    } catch (error) {
      console.error(
        `Error deleting Outbound Rule with ID: ${outboundRule.id}`,
        error
      );
    } finally {
      handleCloseDeleteModal();
    }
  };

  const handleEditSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (selectedOutboundRule) {
      try {
        await updateBXPOutboundRule(
          selectedOutboundRule.id,
          selectedOutboundRule
        );
        await onOutboundRulesChange();
        handleCloseEditModal();
      } catch (error) {
        alert('Error updating outbound rule: ' + error);
      }
    }
  };

  const handleInputChange = (
    e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;
    let newValue: string | boolean | number = value;

    if (type === 'checkbox') {
      newValue = (e.target as HTMLInputElement).checked;
    } else if (type === 'number') {
      newValue = parseInt(value, 10);
    }
    // eslint-disable-next-line
    setSelectedOutboundRule(prev => (prev ? { ...prev, [name]: newValue } : null));
  };

  return (
    <>
      <RevealDropdown btnClassName="p-2 pt-1 pb-1 fs-11">
        <Dropdown.Item onClick={handleShowEditModal}>
          <FontAwesomeIcon icon={faEdit} className="fs-8 p-1 pt-0 pb-0" />
          Edit
        </Dropdown.Item>
        <Dropdown.Item onClick={handleShowDeleteModal} className="text-danger">
          <FontAwesomeIcon icon={faTrash} className="fs-8 p-1 pt-0 pb-0" />
          Remove
        </Dropdown.Item>
      </RevealDropdown>

      <Modal
        show={showDeleteModal}
        onHide={handleCloseDeleteModal}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirm Deletion</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete this Outbound Rule?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseDeleteModal}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleConfirmRemove}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showEditModal}
        onHide={handleCloseEditModal}
        size="lg"
        centered
      >
        <Form onSubmit={handleEditSubmit}>
          <Modal.Header closeButton>
            <Modal.Title>Update Outbound Rule</Modal.Title>
          </Modal.Header>
          <Modal.Body className="px-6">
            <Form.Group className="mb-3">
              <Form.Label>Short Name</Form.Label>
              <Form.Control
                type="text"
                name="shortname"
                value={selectedOutboundRule?.shortname || ''}
                onChange={handleInputChange}
                required
                maxLength={32}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Trunk Type</Form.Label>
              <Form.Control
                type="text"
                name="trunktype"
                value={selectedOutboundRule?.trunktype || ''}
                onChange={handleInputChange}
                maxLength={6}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Trunk Prefix</Form.Label>
              <Form.Control
                type="text"
                name="trunkprefix"
                value={selectedOutboundRule?.trunkprefix || ''}
                onChange={handleInputChange}
                maxLength={32}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Trunk Name</Form.Label>
              <Form.Control
                type="text"
                name="trunkname"
                value={selectedOutboundRule?.trunkname || ''}
                onChange={handleInputChange}
                maxLength={32}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Max Dial Resources</Form.Label>
              <Form.Control
                type="number"
                name="maxdialresources"
                value={selectedOutboundRule?.maxdialresources || 0}
                onChange={handleInputChange}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Dial Timeout</Form.Label>
              <Form.Control
                type="number"
                name="dialtimeout"
                value={selectedOutboundRule?.dialtimeout || 0}
                onChange={handleInputChange}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Cancel Dial Timeout</Form.Label>
              <Form.Control
                type="number"
                name="canceldialtimeout"
                value={selectedOutboundRule?.canceldialtimeout || 0}
                onChange={handleInputChange}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Caps Limit</Form.Label>
              <Form.Control
                type="number"
                name="caps_limit"
                value={selectedOutboundRule?.caps_limit || 0}
                onChange={handleInputChange}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Caps Control Mode</Form.Label>
              <Form.Control
                type="text"
                name="caps_control_mode"
                value={selectedOutboundRule?.caps_control_mode || ''}
                onChange={handleInputChange}
                maxLength={8}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <div className="form-check form-switch pb-1 pt-1">
                <label className="form-check-label">
                  <input
                    type="checkbox"
                    name="active"
                    checked={selectedOutboundRule?.active || false}
                    onChange={handleInputChange}
                    className="form-check-input"
                  />
                  Active
                </label>
              </div>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseEditModal}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              Update Outbound Rule
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </>
  );
};

export const outboundRulesTableColumns: ColumnDef<BXPOutboundRule>[] = [
  {
    accessorKey: 'id',
    header: 'ID',
    meta: {
      headerProps: { style: { width: '5%' }, className: 'px-1 fs-10' },
      cellProps: { className: 'fw-bold px-1 white-space-nowrap' }
    }
  },
  {
    accessorKey: 'shortname',
    header: 'Short Name',
    meta: {
      headerProps: { style: { width: '15%' }, className: 'px-1 fs-10' },
      cellProps: { className: 'px-1 white-space-nowrap' }
    }
  },
  {
    accessorKey: 'action',
    enableSorting: false,
    header: '',
    cell: ({ row: { original } }) => {
      return <OutboundRuleActionCell outboundRule={original} />;
    },
    meta: {
      cellProps: { className: 'text-body-tertiary' }
    }
  },
  {
    accessorKey: 'trunktype',
    header: 'Trunk Type',
    meta: {
      headerProps: { style: { width: '10%' }, className: 'px-1 fs-10' },
      cellProps: { className: 'px-1 white-space-nowrap' }
    }
  },
  {
    accessorKey: 'trunkprefix',
    header: 'Trunk Prefix',
    meta: {
      headerProps: { style: { width: '10%' }, className: 'px-1 fs-10' },
      cellProps: { className: 'px-1 white-space-nowrap' }
    }
  },
  {
    accessorKey: 'trunkname',
    header: 'Trunk Name',
    meta: {
      headerProps: { style: { width: '15%' }, className: 'px-1 fs-10' },
      cellProps: { className: 'px-1 white-space-nowrap' }
    }
  },
  {
    accessorKey: 'maxdialresources',
    header: 'Max Dial Resources',
    meta: {
      headerProps: { style: { width: '10%' }, className: 'px-1 fs-10' },
      cellProps: { className: 'px-1 white-space-nowrap' }
    }
  },
  {
    accessorKey: 'dialtimeout',
    header: 'Dial Timeout',
    meta: {
      headerProps: { style: { width: '10%' }, className: 'px-1 fs-10' },
      cellProps: { className: 'px-1 white-space-nowrap' }
    }
  },
  {
    accessorKey: 'canceldialtimeout',
    header: 'Cancel Dial Timeout',
    meta: {
      headerProps: { style: { width: '10%' }, className: 'px-1 fs-10' },
      cellProps: { className: 'px-1 white-space-nowrap' }
    }
  },
  {
    accessorKey: 'caps_limit',
    header: 'Caps Limit',
    meta: {
      headerProps: { style: { width: '10%' }, className: 'px-1 fs-10' },
      cellProps: { className: 'px-1 white-space-nowrap' }
    }
  },
  {
    accessorKey: 'caps_control_mode',
    header: 'Caps Control Mode',
    meta: {
      headerProps: { style: { width: '10%' }, className: 'px-1 fs-10' },
      cellProps: { className: 'px-1 white-space-nowrap' }
    }
  },
  {
    accessorKey: 'active',
    header: 'Active',
    cell: ({ row }) => (row.original.active ? 'Yes' : 'No'),
    meta: {
      headerProps: { style: { width: '5%' }, className: 'px-1 fs-10' },
      cellProps: { className: 'px-1 white-space-nowrap' }
    }
  }
];

export const BXPOutboundRulesTable: React.FC<OutboundRulesTableProps> = ({
  outboundRules,
  onOutboundRulesChange
}) => {
  const [showAddModal, setShowAddModal] = useState(false);
  const [newOutboundRule, setNewOutboundRule] = useState<BXPOutboundRule>({
    id: 0,
    shortname: '',
    trunktype: '',
    trunkprefix: '',
    trunkname: '',
    maxdialresources: 0,
    dialtimeout: 0,
    canceldialtimeout: 0,
    caps_limit: 0,
    caps_control_mode: '',
    active: false
  });
  const table = useAdvanceTable({
    data: outboundRules,
    columns: outboundRulesTableColumns,
    pageSize: 10,
    pagination: true,
    sortable: true,
    selection: false
  });

  const handleSearchInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    table.setGlobalFilter(e.target.value || undefined);
  };

  const handleSearchKeyPress = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
    }
  };

  const handleShowAddModal = () => setShowAddModal(true);
  const handleCloseAddModal = () => setShowAddModal(false);

  const handleInputChange = (
    e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;
    let newValue: string | boolean | number = value;

    if (type === 'checkbox') {
      newValue = (e.target as HTMLInputElement).checked;
    } else if (type === 'number') {
      newValue = parseInt(value, 10);
    }

    setNewOutboundRule(prev => ({ ...prev, [name]: newValue }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    try {
      await createBXPOutboundRule(newOutboundRule);
      handleCloseAddModal();
      onOutboundRulesChange();
    } catch (error) {
      console.error('Error creating outbound rule:', error);
    }
  };

  return (
    <OutboundRulesChangeContext.Provider value={onOutboundRulesChange}>
      <AdvanceTableProvider {...table}>
        <div className="mb-4">
          <div className="d-flex flex-wrap gap-3">
            <SearchBox
              placeholder="Search Outbound Rules"
              onChange={handleSearchInputChange}
              onKeyDown={handleSearchKeyPress}
            />
            <div className="ms-auto">
              <Button
                variant="primary"
                className="p-2"
                onClick={handleShowAddModal}
              >
                <FontAwesomeIcon icon={faPlus} className="me-2 fs-8" />
                Add Outbound Rule
              </Button>
            </div>
          </div>
        </div>
        <div>
          <AdvanceTable
            tableProps={{ className: 'phoenix-table fs-9' }}
            rowClassName="hover-actions-trigger btn-reveal-trigger position-static"
          />
          <AdvanceTableFooter pagination />
        </div>
      </AdvanceTableProvider>

      <Modal
        show={showAddModal}
        onHide={handleCloseAddModal}
        size="lg"
        centered
      >
        <Form onSubmit={handleSubmit}>
          <Modal.Header closeButton>
            <Modal.Title>Add New Outbound Rule</Modal.Title>
          </Modal.Header>
          <Modal.Body className="px-6">
            <Form.Group className="mb-3">
              <Form.Label>Short Name</Form.Label>
              <Form.Control
                type="text"
                name="shortname"
                value={newOutboundRule.shortname}
                onChange={handleInputChange}
                required
                maxLength={32}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Trunk Type</Form.Label>
              <Form.Control
                type="text"
                name="trunktype"
                value={newOutboundRule.trunktype}
                onChange={handleInputChange}
                maxLength={6}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Trunk Prefix</Form.Label>
              <Form.Control
                type="text"
                name="trunkprefix"
                value={newOutboundRule.trunkprefix}
                onChange={handleInputChange}
                maxLength={32}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Trunk Name</Form.Label>
              <Form.Control
                type="text"
                name="trunkname"
                value={newOutboundRule.trunkname}
                onChange={handleInputChange}
                maxLength={32}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Max Dial Resources</Form.Label>
              <Form.Control
                type="number"
                name="maxdialresources"
                value={newOutboundRule.maxdialresources}
                onChange={handleInputChange}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Dial Timeout</Form.Label>
              <Form.Control
                type="number"
                name="dialtimeout"
                value={newOutboundRule.dialtimeout}
                onChange={handleInputChange}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Cancel Dial Timeout</Form.Label>
              <Form.Control
                type="number"
                name="canceldialtimeout"
                value={newOutboundRule.canceldialtimeout}
                onChange={handleInputChange}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Caps Limit</Form.Label>
              <Form.Control
                type="number"
                name="caps_limit"
                value={newOutboundRule.caps_limit}
                onChange={handleInputChange}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Caps Control Mode</Form.Label>
              <Form.Control
                type="text"
                name="caps_control_mode"
                value={newOutboundRule.caps_control_mode}
                onChange={handleInputChange}
                maxLength={8}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <div className="form-check form-switch pb-1 pt-1">
                <label className="form-check-label">
                  <input
                    type="checkbox"
                    name="active"
                    checked={newOutboundRule.active}
                    onChange={handleInputChange}
                    className="form-check-input"
                  />
                  Active
                </label>
              </div>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseAddModal}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              Add Outbound Rule
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </OutboundRulesChangeContext.Provider>
  );
};
